import React, { useEffect, useMemo, useState } from "react";
import {
  IntegrationCustomFields,
  SetupInstructionsButton,
} from "@SeemplicityDev/shared/integrations";
import "./TicketingSystemInstanceDetails.scss";
import {
  ADD_TICKET_PROVIDER,
  UPDATE_TICKET_PROVIDER,
} from "../TicketingSystemsApi";
import { useTranslation } from "react-i18next";
import {
  useUserAnalytics,
  TextButton,
  useOpenDocWidget,
  useMutationWrapper,
  useIcon,
  showErrorDialog,
  showToast,
  Loader,
} from "@SeemplicityDev/shared";
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import TicketingSystemInstanceTestConnection from "./TicketingSystemInstanceTestConnection/TicketingSystemInstanceTestConnection";

// Instance instructions item shape (read from GraphQL ticket_provider.instructions).
interface InstructionItem {
  title?: string;
  description?: string;
  link?: string;
  link_text?: string;
}

// Single instance under an integration. Loosely shaped because the parent
// (AutomationSystems.tsx) merges GraphQL nodes into Record<string, unknown>;
// only the properties actually read here are declared.
interface Instance {
  id?: string;
  type?: string;
  configuration?: string;
  is_installed?: boolean;
  sections?: unknown;
  instructions?: InstructionItem[];
}

// Integration row passed in via the `integrations` prop. Same loose-shape
// reasoning as above — only the properties read here are declared.
interface Integration {
  id?: string;
  name?: string;
  type?: string;
  sections?: unknown;
  test_connection_supported?: boolean;
  instances?: Instance[];
}

// Payload handed to the OnSubmit callback of IntegrationCustomFields. Only
// `incoming_configuration` is read here; kept optional to stay compatible with
// the shared IntegrationSubmitData contract, where it is optional.
interface InstanceFormData {
  incoming_configuration?: string;
}

// Shape of the mutation results read off the useMutationWrapper responses.
interface EditTicketProviderResult {
  data?: {
    edit_ticket_provider?: {
      ticket_provider?: {
        known_identifier?: string;
      };
    };
  };
}

interface AddTicketProviderResult {
  data?: {
    new_ticket_provider?: {
      ticket_provider?: {
        id?: string;
        display?: string;
        is_installed?: boolean;
        instructions?: InstructionItem[];
      };
    };
  };
}

export interface TicketingSystemInstanceDetailsProps {
  // The parent (AutomationSystems.tsx) models merged rows as
  // Record<string, unknown> (IntegrationToShow); we accept that loose shape and
  // narrow internally via the Integration interface.
  integrations: Record<string, unknown>[];
  loadingTSInstances: boolean;
}

const TicketingSystemInstanceDetails: React.FC<
  TicketingSystemInstanceDetailsProps
> = ({ integrations, loadingTSInstances }) => {
  const { integrationId, instanceId } = useParams<{
    integrationId: string;
    instanceId: string;
  }>();
  const [shouldRunTests, setShouldRunTests] = useState(false);

  const { t } = useTranslation();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { openWidget } = useOpenDocWidget();

  const selectedIntegration = (integrations as Integration[])?.find(
    (integration) => integration.id === integrationId
  );

  const selectedInstance = selectedIntegration?.instances?.find(
    (instance) => instance.id === instanceId
  );
  const selectedInstanceIdInt = useMemo(() => {
    if (!!selectedInstance) {
      return Number(atob(selectedInstance?.id ?? "")?.split(":")?.[1]);
    }
  }, [selectedInstance]);
  const { trackEvent } = useUserAnalytics();
  const { getSvg } = useIcon();

  const [addTicketingSystem] = useMutationWrapper<
    AddTicketProviderResult["data"]
  >(ADD_TICKET_PROVIDER, {
    refetchQueries: ["GetTicketProviders"],
  });

  const [updateTicketingSystemInstance] = useMutationWrapper<
    EditTicketProviderResult["data"]
  >(UPDATE_TICKET_PROVIDER, {
    refetchQueries: ["GetTicketProviders"],
  });

  const handleEditing = (existingInstanceData: InstanceFormData) => {
    const existingTicketingData = {
      ticket_provider_id: selectedInstance?.id,
      incoming_configuration: existingInstanceData.incoming_configuration,
    };

    return updateTicketingSystemInstance({ variables: existingTicketingData })
      .then((res) => {
        if (res?.data?.edit_ticket_provider?.ticket_provider) {
          showToast({
            message: (
              <div className={"modal-text"}>
                The Instance{" "}
                <span className={"modal-live-text"}>
                  {
                    res?.data?.edit_ticket_provider?.ticket_provider
                      ?.known_identifier
                  }
                </span>{" "}
                was updated successfully
              </div>
            ),
          });
        }
      })
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      });
  };

  const handleAddition = (newInstanceData: InstanceFormData) => {
    trackEvent(`Integrations_Save_Integration_${selectedIntegration?.name}`);

    const newTicketingData = {
      ticket_provider_type: selectedIntegration?.type,
      incoming_configuration: newInstanceData.incoming_configuration,
    };

    return addTicketingSystem({ variables: newTicketingData })
      .then((res) => {
        let confirmationMsg: React.ReactNode = "";
        if (!res.data?.new_ticket_provider?.ticket_provider?.is_installed) {
          confirmationMsg = (
            <div className={"modal-text"}>
              The Instance
              <span className={"modal-live-text"}>
                {res?.data?.new_ticket_provider?.ticket_provider?.display}
              </span>{" "}
              was added successfully. Please follow the instructions for further
              configuration.
            </div>
          );
        } else {
          confirmationMsg = (
            <div className={"modal-text"}>
              The Instance
              <span className={"modal-live-text"}>
                {res?.data?.new_ticket_provider?.ticket_provider?.display}
              </span>{" "}
              was added successfully
            </div>
          );
        }
        showToast({
          message: confirmationMsg,
        });
        const newPath = generatePath(path, {
          integrationId: integrationId,
          instanceId: res?.data?.new_ticket_provider?.ticket_provider?.id,
        });
        history.replace(newPath);
        if (!res?.data?.new_ticket_provider?.ticket_provider?.instructions) {
          setShouldRunTests(true);
        }
      })
      .catch((err: Error) => {
        showErrorDialog({ message: err.message });
      });
  };

  const isNewInstance = useMemo(() => instanceId === "new", [instanceId]);

  useEffect(() => {
    if (isNewInstance) {
      openWidget();
    }
  }, [isNewInstance]);

  return loadingTSInstances ? (
    <div className={"full-height"}>
      <Loader
        center
        className={"spinner-img"}
        img={getSvg("icon-spinner")}
        iconSize={"xxxxl"}
        wrapperHeight
      />
    </div>
  ) : (
    <>
      <SetupInstructionsButton
        integration={selectedIntegration}
        data-testid={"setup-instructions-btn"}
      />

      <h4 className={"margin-top-20 margin-bottom-15"}>
        {t("Instance Details")}
      </h4>
      <IntegrationCustomFields
        submitBtnText={"Confirm"}
        OnSubmit={isNewInstance ? handleAddition : handleEditing}
        shouldDisplayCancel={false}
        integrationDefinitionId={selectedIntegration?.id}
        integrationDefinitionSections={
          (selectedInstance?.sections ||
            selectedIntegration?.sections) as React.ComponentProps<
            typeof IntegrationCustomFields
          >["integrationDefinitionSections"]
        }
        fieldsValues={
          selectedInstance
            ? JSON.parse(selectedInstance?.configuration ?? "")
            : undefined
        }
        instanceId={selectedInstance?.id}
        wrapperClassName={"ts-form-btns"}
        currentIntegration={selectedIntegration}
        useDisableFieldOnEdit
      />
      {selectedInstance?.instructions?.map((instructionItem) => (
        <>
          <h4 className={"margin-top-40"}>{instructionItem.title}</h4>
          <p
            className={"instruction-description margin-top-10 margin-bottom-20"}
          >
            {instructionItem.description}
          </p>
          {instructionItem.link_text && (
            <TextButton
              data-testid={"user-actions-submit"}
              label={instructionItem.link_text}
              buttonStyle={"primary"}
              onClick={() => window.open(instructionItem.link, "_blank")}
            />
          )}
        </>
      ))}
      {selectedInstance?.is_installed &&
        selectedIntegration?.test_connection_supported && (
          <TicketingSystemInstanceTestConnection
            idInt={selectedInstanceIdInt}
            id={selectedInstance?.id}
            runTestsOnLoad={shouldRunTests}
            type={selectedInstance?.type}
          />
        )}
    </>
  );
};
export default TicketingSystemInstanceDetails;
